import { deleteStudyById, getAllstudy } from "@api/study";
import useAuth from "@hooks/Auth/useAuth";
import { usePermissionMutation } from "@hooks/Permission/usePermissionMutation";
import useModalActions from "@hooks/common/useModalAction";
import { Study } from "@models/Study";
import { APIError } from "@models/index";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toCamelCase } from "utils/caseConverter";
import { normalizeDICOMPersonName } from "utils/index";
import { useMemo } from "react";

interface useStudyGroupProps {
  page?: number;
  getEnabled?: boolean;
  limit?: number;
  filters?: object;
  sort?: object;
  onSuccess?: () => void;
  onError?: () => void;
}

export default function useStudyGroup({
  page = 1,
  limit = 10,
  filters = {},
  sort = {},
  getEnabled = true,
}: useStudyGroupProps) {
  const { accessToken } = useAuth();

  const {
    data, // 성공적으로 가져온 데이터
    isLoading, // 쿼리 함수의 첫 번째 가져오기가 진행중인지 여부
    isError, //쿼리 함수에서의 오류 발생 여부
    isSuccess, // 쿼리 데이터를 성공적으로 가져왔는지 여부
    isFetching, //쿼리의 첫 데이터 가져오기가 완료되었는지 여부
    refetch, // 쿼리 데이터를 성공적으로 가져왔는지 여부
    error, // 오류가 발생했을 때의 오류 객체, 오류가 발생하지 않았다면 null
  } = useQuery({
    queryKey: ['studyGroup', page, limit, filters, sort],
    queryFn: () =>
      getAllstudy({
        accessToken,
        params: { page, limit, filters, sort },
      }),
    enabled: getEnabled && !!accessToken,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: 3000,
  });

  const study = useMemo(() => {
    const rawData = data?.data?.data ?? [];
    const camelData = toCamelCase<Study[]>(rawData);

    // const camelData = toCamelCase<Study[]>(rawData).map((study) => ({
    //   ...study,
    //   patient: study.patient
    //     ? {
    //         ...study.patient,
    //         patientName: study.patient.patientName
    //           ? normalizeDICOMPersonName(study.patient.patientName)
    //           : study.patient.patientName,
    //       }
    //     : study.patient,
    // }));
    const totalCount = data?.data?.count ?? false;
    const ishasNextPage = data?.data?.hasNextPage ?? false;
    return {
      data: camelData,
      count: totalCount,
      hasNextPage: ishasNextPage,
    };
  }, [data]);

  return {
    study,
    isLoading,
    isError,
    isSuccess,
    isFetching,
    refetch,
    error,
  };
}

export function useDeleteStudyById() {
  const queryClient = useQueryClient();
  const { accessToken } = useAuth();
  const { selectAction, errorAction } = useModalActions();

  return usePermissionMutation<void, string>({
    mutationFn: async (id: string) => {
      await new Promise<void>((resolve, reject) => {
        selectAction('Are you sure you want to delete this study?', async () => {
          try {
            await deleteStudyById({ id, accessToken });
            resolve();
          } catch (err) {
            reject(err);
          }
        });
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['studyGroup'] });
    },
    onError: (error) => {
      errorAction(error as APIError, 'Failed to delete study. Please contact the administrator.');
    },
  });
}
